import Grid from "@mui/material/Grid"; // Add Grid import
import Icon from "@mui/material/Icon";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import PropTypes from "prop-types";
import { useState } from "react";

// @mui material components

// Argon Dashboard 2 PRO MUI components
import TaskModal from "0.Mine/0.Components/CRM/Leads/TaskModal";
import { UseHandleKanban } from "0.Mine/1.Hooks/CRM/Kanban";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";
import ArgonAvatar from "components/ArgonAvatar";
import ArgonBadge from "components/ArgonBadge";
import ArgonBox from "components/ArgonBox";
import ArgonProgress from "components/ArgonProgress";
import ArgonTypography from "components/ArgonTypography";

// CRM Todo Modal

// Context
import { useArgonController } from "context";

function Card({
  image,
  //badge,
  content,
  progress,
  attachedFiles,
  members = [],
  onClick,
  statusOptions,
  vendors,
  id,
  badge,
  interactions,
  assignedTo,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [controller] = useArgonController();
  const { darkMode } = controller;
  const { getOneKanbanCard } = UseHandleKanban();
  const { session } = useBusinessContext();
  const [lead, setLead] = useState({});
  if (!session || !session.selectedBusiness) return <p>Please select a business to get started</p>;
  const handleClick = async () => {
    setIsModalOpen(true);
    console.log("VENDORS", vendors);
    if (onClick) onClick();
    const response = await getOneKanbanCard(session.selectedBusiness.id, id);
    if (response) {
      setLead(response);
    } else {
      console.error("Error fetching lead data:", response);
    }
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const renderMembers = members.map((member, key) => (
    <ArgonAvatar
      key={`image-${key}`}
      src={member}
      alt={`image-${key}`}
      size="xs"
      sx={{
        border: ({ borders: { borderWidth }, palette: { white } }) =>
          `${borderWidth[2]} solid ${white.main}`,
        cursor: "pointer",
        position: "relative",
        ml: -1,
        "&:hover, &:focus": { zIndex: "10" },
      }}
    />
  ));

  return (
    <>
      <Paper
        onClick={handleClick}
        sx={{
          padding: 2,
          backgroundColor: darkMode ? "#1a2035" : "#ffffff",
          borderRadius: "15px",
          boxShadow: "0 2px 12px 0 rgba(0,0,0,0.1)",
          transition: "all 0.2s ease-in-out",
          marginTop: "8px",
          width: "100%",
          cursor: "pointer",
          position: "relative",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
          },
        }}
      >
        {image && <ArgonBox component="img" src={image} width="100%" borderRadius="sm" mb={1} />}
        {badge && <ArgonBadge size="xs" color={badge.color} badgeContent={badge.label} container />}
        <ArgonBox mt={1} mb={2}>
          <ArgonTypography variant="body2" color="text">
            {content}
          </ArgonTypography>
        </ArgonBox>

        {/* Footer grid with assigned to and interaction count */}
        <Grid container spacing={1} alignItems="center">
          {/* Assigned to section */}
          <Grid item xs={8}>
            {assignedTo && (
              <ArgonBox display="flex" alignItems="center">
                <ArgonAvatar
                  src={assignedTo.image || ""}
                  alt={assignedTo.name || "User"}
                  size="xs"
                  sx={{
                    border: ({ borders: { borderWidth }, palette: { white } }) =>
                      `${borderWidth[2]} solid ${white.main}`,
                    mr: 1,
                  }}
                />
                <ArgonTypography variant="caption" color="text" fontWeight="medium">
                  {assignedTo.name || " "}
                </ArgonTypography>
              </ArgonBox>
            )}
          </Grid>

          {/* Interactions counter */}
          <Grid item xs={4}>
            <ArgonBox display="flex" alignItems="center" justifyContent="flex-end">
              <Tooltip title="Numero Interazioni">
                <ArgonBox display="flex" alignItems="center">
                  <Icon
                    sx={{
                      fontSize: 16,
                      color: darkMode ? "rgba(255,255,255,0.7)" : "text.secondary",
                      mr: 0.5,
                    }}
                  >
                    forum
                  </Icon>
                  <ArgonTypography
                    variant="caption"
                    color={darkMode ? "rgba(255,255,255,0.7)" : "text.secondary"}
                    fontWeight="medium"
                  >
                    {interactions || 0}
                  </ArgonTypography>
                </ArgonBox>
              </Tooltip>
            </ArgonBox>
          </Grid>

          {/* Original members section if still needed */}
          {members.length > 0 && (
            <Grid item xs={12}>
              <ArgonBox display="flex">{renderMembers}</ArgonBox>
            </Grid>
          )}
        </Grid>
      </Paper>

      <TaskModal
        open={isModalOpen}
        onClose={handleCloseModal}
        initialStatusOptions={statusOptions}
        vendors={vendors}
        cardId={id}
        lead={lead}
      />
    </>
  );
}

Card.defaultProps = {
  image: "",
  progress: 0,
  attachedFiles: "",
  members: [],
  onClick: () => {},
};

Card.propTypes = {
  image: PropTypes.string,
  badge: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]).isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  content: PropTypes.node.isRequired,
  progress: PropTypes.number,
  attachedFiles: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  members: PropTypes.arrayOf(PropTypes.string),
  onClick: PropTypes.func,
  statusOptions: PropTypes.array,
  vendors: PropTypes.array,
  id: PropTypes.string,
  badge: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
    label: PropTypes.string.isRequired,
  }).isRequired,
  interactions: PropTypes.number,
  assignedTo: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.string,
  }),
};

export default Card;
